import {
    DropdownButtonType,
    List,
    ListItem,
    ListProps,
    SingleSelectDropdown,
    TabStyles} from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { CredentialOption } from '~src/connectivity/components/EditConnection/IEditConnectionDialog';
import { IOAuthCredential } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';

// W-22483245: the "Select an Account" dropdown shared by the stock OAuth picker and the unified
// WIF-eligible chooser. Presentational only: the owning dialog supplies the account list and the
// change handler (including any add-new-credential behavior), so the dropdown carries no fetch logic.
export type OAuthAccountDropdownProps = Readonly<{
    credentialOption: CredentialOption;
    credentials: IOAuthCredential[];
    credentialId: string;
    credentialNoValueOptionText: string;
    addNewOAuthCredentialOptionValue: string;
    currentConnectionTypeDisplayName: string;
    ariaLabel: string;
    handleCredentialIdChange: (newCredentialId: string) => void;
}>;

export function OAuthAccountDropdown(props: OAuthAccountDropdownProps) {
    // The account picker only applies to credential-bearing auth models; password / none / mixed have
    // no account to select.
    if (props.credentialOption !== 'oauth' && props.credentialOption !== 'keypair') {
        return null;
    }

    function getIOAuthCredentialIdsList(ioauthCredentials: IOAuthCredential[]): ListItem[] {
        const items: ListItem[] = [];
        if (ioauthCredentials) {
            ioauthCredentials.forEach((ioauthCredential) => {
                items.push({ value: ioauthCredential.id, content: ioauthCredential.role ? ioauthCredential.username + (ioauthCredential.role && ' (' + ioauthCredential.role + ')') : ioauthCredential.username });
            });
        }
        return items;
    }

    function getNameForIOAuthCredentialId(value: string, items: ListItem[]): React.ReactNode {
        const match = items.find(item => item.value === value);
        return match ? match.content : (props.credentialNoValueOptionText || Messages.selectOAuthCredential());
    }

    let items: ListItem[] = [];
    if (props.credentials) {
        items = getIOAuthCredentialIdsList(props.credentials);
    }
    if (props.addNewOAuthCredentialOptionValue) {
        items.push({ value: props.addNewOAuthCredentialOptionValue,
            content: Messages.addOAuthCredential({ CONNECTION_TYPE_NAME: props.currentConnectionTypeDisplayName +
                (props.credentialOption === 'keypair' ? ' '.concat(Messages.addNewCredential_KeyPairAuth()) : '') }) });
    }
    const listProps: ListProps = { items };

    return (
        <SingleSelectDropdown
            aria-label={props.ariaLabel}
            label=''
            list={<List {...listProps} />}
            onChange={props.handleCredentialIdChange}
            type={DropdownButtonType.Outline}
            value={props.credentialId}
            style={{ marginBottom: TabStyles.Sizing.BaseUnit }}
        >
            {getNameForIOAuthCredentialId(props.credentialId, items)}
        </SingleSelectDropdown>
    );
}
